import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TheBorderBitmapText} from "../components/TheBorderBitmapText";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {gsap} from "gsap";
import {TheBorderSymbol} from "../reels/TheBorderSymbol";
import {TheBorder} from "../TheBorder";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TheBorderStatics} from "../TheBorderStatics";
import {TheBorderTimelineSprite} from "../components/TheBorderTimelineSprite";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {IComboWinData} from "../server/TheBorderData";
import {TheBorderXNudgeSymbol} from "../reels/TheBorderXNudgeSymbol";
import {ISymbol} from "../../core/reel/ReelInterfaces";
import {TheBorderReelModel} from "../reels/TheBorderReelModel";

/**
 * Created by Jerker Nord on 2022-04-19.
 */

export class TheBorderClusterWinView extends PIXI.Container implements StageLayer {
    private static WIRE_TOP_OFFSET:PIXI.Point = new PIXI.Point(0, -49);
    private static WIRE_BOTTOM_OFFSET:PIXI.Point = new PIXI.Point(0, 50);
    private static DELAY_BETWEEN_COMBO_WINS:number = 0.3;
    private static WIN_ABOVE_LEVEL_01:number = 50;

    private _clusterWinAmountTexts:TheBorderBitmapText[];
    private _symbolExplodeAnimations:TimelineSprite[][];
    private _wireLeftSideAnimations:TheBorderTimelineSprite[];
    private _wireHalfLeftSideAnimations:TheBorderTimelineSprite[];
    private _wireRightSideAnimations:TheBorderTimelineSprite[];
    private _wireHalfRightSideAnimations:TheBorderTimelineSprite[];
    private _wireTopAnimations:TheBorderTimelineSprite[];
    private _wireBottomAnimations:TheBorderTimelineSprite[];
    private _wireTextures:PIXI.Texture[];
    private _wireHalfTextures:PIXI.Texture[];
    private _tapeContainer:PIXI.Container;

    constructor() {
        super();
        this.name = "TheBorderClusterWinView";
        this.init();
    }

    public getDarkDownNoWinAnimation(allExplodePositions:boolean[][], xBombExplodePos:boolean[][][]):gsap.core.Timeline {
        let mainTL:gsap.core.Timeline = gsap.timeline();
        let subDarkDownSymbolsTLs:gsap.core.Timeline[] = [];
        let duration:number = 0.4;
        const tapeTweens:gsap.core.Tween[] = [];

        let allTapes:PIXI.Sprite[] = this.getAllTapeAnimations(xBombExplodePos);
        //Different timing if it's only xbombs and no win
        if(TheBorder.model.betClusterWins.length == 0) {
            duration = 0.4;
        }
        for(let i:number = 0; i < allExplodePositions.length; i++) {
            for(let j:number = 0; j < allExplodePositions[i].length; j++) {
                if(!allExplodePositions[i][j]) {
                    if(TheBorder.allReelsView.mainReelModels[i].getSymbol(j)) {
                        subDarkDownSymbolsTLs.push((<TheBorderSymbol>TheBorder.allReelsView.mainReelModels[i].getSymbol(j)).getNoWinAnimation(duration));
                    }
                }
            }
        }
        allTapes.forEach((tape:PIXI.Sprite) => {
            tapeTweens.push(gsap.to(tape, 0.1, {alpha : 1}));
        });
        mainTL.add(subDarkDownSymbolsTLs);
        mainTL.add(tapeTweens, 0);

        return mainTL;
    }

    public getDarkDownNoWinEndAnimation(allExplodePositions:boolean[][]):gsap.core.Timeline {
        let mainTL:gsap.core.Timeline = gsap.timeline();
        let subDarkDownSymbolsTLs:gsap.core.Timeline[] = [];
        for(let i:number = 0; i < allExplodePositions.length; i++) {
            for(let j:number = 0; j < allExplodePositions[i].length; j++) {
                if(!allExplodePositions[i][j]) {
                    const darkDownSymbolTL:gsap.core.Timeline = gsap.timeline();
                    if(TheBorder.allReelsView.mainReelModels[i].getSymbol(j)) {
                        darkDownSymbolTL.add((<TheBorderSymbol>TheBorder.allReelsView.mainReelModels[i].getSymbol(j)).getNoWinEndAnimation());
                    }
                    subDarkDownSymbolsTLs.push(darkDownSymbolTL);
                }
            }
        }
        mainTL.add(subDarkDownSymbolsTLs);

        return mainTL;
    }

    //Counts all the wins that are below 50 * base bet
    private calculateAboveLevel01LingerTime(comboWinData:IComboWinData[]):number {
        let belowLevelWins:number = 0;
        for(let i:number = 0; i < comboWinData.length; i++) {
            if(comboWinData[i].amount < (TheBorderClusterWinView.WIN_ABOVE_LEVEL_01 * TheBorder.model.playedBetValue)) {
                belowLevelWins++;
            }
        }
        return 0.5 + (belowLevelWins * TheBorderClusterWinView.DELAY_BETWEEN_COMBO_WINS);
    }

    public getComboWinAmountAnimations(comboWinData:IComboWinData[]):gsap.core.Timeline {
        let mainTL:gsap.core.Timeline = gsap.timeline();
        let moveUpDuration:number;
        let scaleUpAmount:number;
        let scaleUpDuration:number;
        let moveUpAmount:number;
        let lingerTime:number;
        let winAboveLevel01LingerTime:number = this.calculateAboveLevel01LingerTime(comboWinData);
        let comboWinTLs:gsap.core.Timeline [] = [];
        for(let i:number = 0; i < comboWinData.length; i++) {
            moveUpDuration = 0.5;
            scaleUpAmount = 1;
            scaleUpDuration = 0.5;
            moveUpAmount = 100;
            lingerTime = 0;
            const subComboWinTL:gsap.core.Timeline = gsap.timeline({delay : (i * TheBorderClusterWinView.DELAY_BETWEEN_COMBO_WINS)}),
                reelPosition:PIXI.Point = comboWinData[i].reelPosition,
                reelId:number = reelPosition.x,
                rowId:number = reelPosition.y;
            let comboWinText:TheBorderBitmapText = this.getUnusedWinText();
            comboWinText.position.x = (reelId * 120);
            comboWinText.position.y = ((TheBorder.allReelsView.getSymbolAtPosition(reelId, rowId) as TheBorderSymbol).y);
            if(comboWinData[i].amount > (TheBorderClusterWinView.WIN_ABOVE_LEVEL_01 * TheBorder.model.playedBetValue)) {
                lingerTime = winAboveLevel01LingerTime;
                scaleUpAmount = 1.2;
            }
            subComboWinTL.add(() => {
                comboWinText.text = SlotGame.apiPlugin.currency.formatValue(comboWinData[i].amount);
                //Making sure text doesn't go outside screen in portrait mode
                if(reelId == 0 && comboWinText.width > 100) {
                    comboWinText.position.x += (comboWinText.width - 100) / 2;
                }
            });
            subComboWinTL.add([
                gsap.to(comboWinText, {duration : 0.1, alpha : 1}),
                gsap.to(comboWinText.scale, {duration : scaleUpDuration, x : scaleUpAmount, y : scaleUpAmount}),
                gsap.to(comboWinText, {duration : moveUpDuration, y : comboWinText.position.y - moveUpAmount})
            ]);
            subComboWinTL.add(gsap.to(comboWinText, {duration : 0.1, alpha : 0, delay : lingerTime}));
            if(lingerTime > 0) {
                subComboWinTL.add(gsap.to(comboWinText.scale, {duration : 0.1, x : 1, y : 1}), "-=0.15");
            }
            subComboWinTL.add(() => {
                comboWinText.scale.set(1, 1);
                comboWinText.used = false;
            });
            comboWinTLs.push(subComboWinTL);
        }

        mainTL.add(comboWinTLs);

        return mainTL;
    }

    public getClusterWinAnimation(clusterWinData:PIXI.Point[]):gsap.core.Timeline {
        let mainTL:gsap.core.Timeline = gsap.timeline({
            onComplete : () => {
                this.hideAll();
            }
        });

        let subShowWireTLs:gsap.core.Timeline[] = [];

        let allWires:TheBorderTimelineSprite[] = this.getAllWireAnimations(TheBorder.model.getAllWinPos());
        for(let i:number = 0; i < allWires.length; i++) {
            const subShowWireTL:gsap.core.Timeline = gsap.timeline();
            subShowWireTL.add(gsap.to(allWires[i], {duration : 0.5, alpha : 1}));
            subShowWireTLs.push(subShowWireTL);
        }

        let subRemoveSymbolsTLs:gsap.core.Timeline[] = [];
        let reel:number;
        let row:number;
        for(let i:number = 0; i < clusterWinData.length; i++) {
            const removeWinSymbolsTL:gsap.core.Timeline = gsap.timeline();
            reel = clusterWinData[i].x;
            row = clusterWinData[i].y;

            let symbol:TheBorderSymbol = <TheBorderSymbol>TheBorder.allReelsView.mainReelModels[reel].getSymbol(row);
            let animation:TimelineSprite = this._symbolExplodeAnimations[reel][row];
            animation.position.y = symbol.position.y;

            if(symbol && !symbol.isRemoved && symbol.symbolName.indexOf("X_BOMB") === -1) {
                symbol.isRemoved = true;
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.25, alpha : 1}));
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.25, alpha : 0}));
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.20, alpha : 1}));
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.20, alpha : 0}));
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.10, alpha : 1}));
                removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.10, alpha : 0}));

                if(this.playLongerAnimation() == true) {
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.08, alpha : 1}));
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.08, alpha : 0}));
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.06, alpha : 1}));
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.06, alpha : 0}));
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.04, alpha : 1}));
                    removeWinSymbolsTL.add(gsap.to(symbol.winAddonSprite, {duration : 0.04, alpha : 0}));
                }

                removeWinSymbolsTL.add(() => {
                    symbol.winAddonSprite.alpha = 0;

                    if(symbol.sizeMultiplier === 0.5) {
                        animation.scale.y = 0.5;
                        animation.y = (symbol.y - 25);
                    } else {
                        animation.scale.y = 1;
                        animation.y = (symbol.y);
                    }
                    animation.visible = true;
                });
                removeWinSymbolsTL.add(animation.getAnimation([0, 5]).timeScale(1.5));
                removeWinSymbolsTL.add(() => {
                    TheBorder.allReelsView.mainReelModels[symbol.reelId].reelView.removeAvalancheSymbol(symbol, false);

                    if(symbol.symbolName.indexOf("WR") > -1) {
                        (symbol as TheBorderXNudgeSymbol).turnOffSymbolSpine();
                        // #243
                        symbol.splitLevelContainer.visible = false;
                    }
                });
                removeWinSymbolsTL.add(animation.getAnimation([6, 32]).timeScale(1.5));
                removeWinSymbolsTL.add(() => {
                    animation.visible = false;
                });
            }
            subRemoveSymbolsTLs.push(removeWinSymbolsTL);
        }

        mainTL.add([
            gsap.timeline().add(subShowWireTLs),
            gsap.timeline({delay : 0.1}).add(subRemoveSymbolsTLs)
        ]);

        mainTL.add(() => {
            for(let i:number = 0; i < allWires.length; i++) {
                allWires[i].alpha = 0;
                allWires[i].used = false;
            }
            this.removeTape();
        }, "-=0.7");

        return mainTL;
    }

    private playLongerAnimation():boolean {
        if(((TheBorder.model.data.mode.indexOf("NORMAL") > -1) || (TheBorder.model.data.mode.indexOf("FREESPIN") > -1
            && TheBorder.model.data.mode.indexOf("SUPER") == -1))) {
            if(TheBorder.model.thisSpinWinnings / TheBorder.model.playedBetValue >= 15) {
                return true;
            }
        }
        if(TheBorder.model.data.mode.indexOf("SUPER") > -1) {
            if(TheBorder.model.thisSpinWinnings / TheBorder.model.playedBetValue >= 100) {
                return true;
            }
        }

        return false;
    }

    public removeTape():void {
        this._tapeContainer.removeChildren();
    }

    public hideAll():void {
        const allWires:TheBorderTimelineSprite[][] = [this._wireTopAnimations, this._wireBottomAnimations, this._wireLeftSideAnimations, this._wireRightSideAnimations, this._wireHalfLeftSideAnimations, this._wireHalfRightSideAnimations];

        allWires.forEach((wires:TheBorderTimelineSprite[]) => {
            for(let i:number = 0; i < wires.length; i++) {
                wires[i].alpha = 0;
                wires[i].used = false;
            }
        });
        this.removeTape();
    }

    private init():void {
        this._tapeContainer = new PIXI.Container();
        this._tapeContainer.name = "TAPE_CONTAINER";
        this.addChild(this._tapeContainer);
        this._wireTextures = TheBorder.assetLoader.getTextures("Bwire/Bwire_");
        this._wireHalfTextures = TheBorder.assetLoader.getTextures("BwireHalf_");
        this._wireTopAnimations = [];
        this._wireLeftSideAnimations = [];
        this._wireBottomAnimations = [];
        this._wireRightSideAnimations = [];
        this._wireHalfRightSideAnimations = [];
        this._wireHalfLeftSideAnimations = [];
        for(let i:number = 0; i < 20; i++) {
            this._wireTopAnimations.push(this.createWireTopAnimation());
            this._wireLeftSideAnimations.push(this.createWireLeftAnimation());
            this._wireHalfLeftSideAnimations.push(this.createWireHalfLeftAnimation());
            this._wireBottomAnimations.push(this.createWireBottomAnimation());
            this._wireRightSideAnimations.push(this.createWireRightAnimation());
            this._wireHalfRightSideAnimations.push(this.createWireHalfRightAnimation());
        }

        this._symbolExplodeAnimations = [];
        let tempExplodeAnimation:TimelineSprite;
        for(let i:number = 0; i < 6; i++) {
            const subExplodeAnimations:TimelineSprite[] = [];
            for(let j:number = 0; j < 12; j++) {
                tempExplodeAnimation = new TimelineSprite(TheBorder.assetLoader.getTextures("Symbol_blast/Symbol_blast_"));
                tempExplodeAnimation.position.x = (i * TheBorderStatics.symbolWidth);
                tempExplodeAnimation.anchor.set(0.5, 0.5);
                tempExplodeAnimation.blendMode = PIXI.BLEND_MODES.SCREEN;
                tempExplodeAnimation.visible = false;
                this.addChild(tempExplodeAnimation);

                subExplodeAnimations.push(tempExplodeAnimation);
            }
            this._symbolExplodeAnimations.push(subExplodeAnimations);
        }

        this._clusterWinAmountTexts = [];
        for(let i:number = 0; i < 10; i++) {
            this._clusterWinAmountTexts.push(this.createWinText());
        }

        this.position = TheBorderStatics.MAIN_REELS_VIEW_POSITION;
    }

    private createWireTopAnimation():TheBorderTimelineSprite {
        let wireTop:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireTextures);
        wireTop.anchor.set(0.5, 0.5);
        wireTop.alpha = 0;
        wireTop.playLoop();
        this.addChild(wireTop);
        return wireTop;
    }

    private createWireBottomAnimation():TheBorderTimelineSprite {
        let wireBottom:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireTextures);
        wireBottom.anchor.set(0.5, 0.5);
        wireBottom.alpha = 0;
        wireBottom.playLoop();
        wireBottom.angle = 180;
        this.addChild(wireBottom);
        return wireBottom;
    }

    private createWireLeftAnimation():TheBorderTimelineSprite {
        let wireLeft:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireTextures);
        wireLeft.anchor.set(0.5, 0.5);
        wireLeft.alpha = 0;
        wireLeft.width = Math.floor(wireLeft.width * 0.85);
        wireLeft.angle = 270;
        wireLeft.playLoop();
        this.addChild(wireLeft);
        return wireLeft;
    }

    private createWireHalfLeftAnimation():TheBorderTimelineSprite {
        let wireHalfLeft:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireHalfTextures);
        wireHalfLeft.anchor.set(0.5, 0.5);
        wireHalfLeft.alpha = 0;
        wireHalfLeft.width = Math.floor(wireHalfLeft.width * 0.85);
        wireHalfLeft.angle = 270;
        wireHalfLeft.playLoop();
        this.addChild(wireHalfLeft);
        return wireHalfLeft;
    }

    private createWireRightAnimation():TheBorderTimelineSprite {
        let wireRight:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireTextures);
        wireRight.anchor.set(0.5, 0.5);
        wireRight.alpha = 0;
        wireRight.width = Math.floor(wireRight.width * 0.85);
        wireRight.angle = 90;
        wireRight.playLoop();
        this.addChild(wireRight);
        return wireRight;
    }

    private createWireHalfRightAnimation():TheBorderTimelineSprite {
        let wireHalfRight:TheBorderTimelineSprite = new TheBorderTimelineSprite(this._wireHalfTextures);
        wireHalfRight.anchor.set(0.5, 0.5);
        wireHalfRight.alpha = 0;
        wireHalfRight.width = Math.floor(wireHalfRight.width * 0.85);
        wireHalfRight.angle = 90;
        wireHalfRight.playLoop();
        this.addChild(wireHalfRight);
        return wireHalfRight;
    }

    private createWinText():TheBorderBitmapText {
        let clusterWinAmountText:TheBorderBitmapText = new TheBorderBitmapText("", {
            fontName : TheBorderGameAssets.BITMAP_FONT_WIN_NUMBERS,
            fontSize : 62
        });
        clusterWinAmountText.anchor = 0.5;
        clusterWinAmountText.alpha = 0;
        this.addChild(clusterWinAmountText);
        return clusterWinAmountText;
    }

    private getUnusedTopWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireTopAnimations.length; i++) {
            if(!this._wireTopAnimations[i].used) {
                this._wireTopAnimations[i].used = true;
                return this._wireTopAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireTopAnimations.push(this.createWireTopAnimation());
        return this.getUnusedTopWire();
    }

    private getUnusedBottomWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireBottomAnimations.length; i++) {
            if(!this._wireBottomAnimations[i].used) {
                this._wireBottomAnimations[i].used = true;
                return this._wireBottomAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireBottomAnimations.push(this.createWireBottomAnimation());
        return this.getUnusedBottomWire();
    }

    private getUnusedLeftWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireLeftSideAnimations.length; i++) {
            if(!this._wireLeftSideAnimations[i].used) {
                this._wireLeftSideAnimations[i].used = true;
                return this._wireLeftSideAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireLeftSideAnimations.push(this.createWireLeftAnimation());
        return this.getUnusedLeftWire();
    }

    private getUnusedLeftHalfWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireHalfLeftSideAnimations.length; i++) {
            if(!this._wireHalfLeftSideAnimations[i].used) {
                this._wireHalfLeftSideAnimations[i].used = true;
                return this._wireHalfLeftSideAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireHalfLeftSideAnimations.push(this.createWireHalfLeftAnimation());
        return this.getUnusedLeftHalfWire();
    }

    private getUnusedRightHalfWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireHalfRightSideAnimations.length; i++) {
            if(!this._wireHalfRightSideAnimations[i].used) {
                this._wireHalfRightSideAnimations[i].used = true;
                return this._wireHalfRightSideAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireHalfRightSideAnimations.push(this.createWireHalfRightAnimation());
        return this.getUnusedRightHalfWire();
    }

    private getUnusedRightWire():TheBorderTimelineSprite {
        for(let i:number = 0; i < this._wireRightSideAnimations.length; i++) {
            if(!this._wireRightSideAnimations[i].used) {
                this._wireRightSideAnimations[i].used = true;
                return this._wireRightSideAnimations[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._wireRightSideAnimations.push(this.createWireRightAnimation());
        return this.getUnusedRightWire();
    }

    private getUnusedWinText():TheBorderBitmapText {
        for(let i:number = 0; i < this._clusterWinAmountTexts.length; i++) {
            if(!this._clusterWinAmountTexts[i].used) {
                this._clusterWinAmountTexts[i].used = true;
                return this._clusterWinAmountTexts[i];
            }
        }
        //If all cached are in use, create a new one and try again
        this._clusterWinAmountTexts.push(this.createWinText());
        return this.getUnusedWinText();
    }

    private getAllWireAnimations(winPosMap:Map<number, number[]>):TheBorderTimelineSprite[] {
        const wireAnimations:TheBorderTimelineSprite[] = [];
        this.addWireToReel(winPosMap, wireAnimations);

        return wireAnimations;
    }

    /**
     * Add Left or Right Tape if connected win
     * */
    private addLeftRightTape(currentSym:TheBorderSymbol, allWinPosMap:Map<number, number[]>, isRightDirection:boolean, tapeAnimations:PIXI.Sprite[]):void {
        const currentReelId:number = currentSym.reelId;
        let isTopHalf:boolean = true;
        let isBottomHalf:boolean = true;
        let comparedReelId:number;

        if(isRightDirection) {
            comparedReelId = currentReelId + 1;
        } else {
            comparedReelId = currentReelId - 1;
        }
        if(comparedReelId < 6 && comparedReelId >= 0) {
            const toBeComparedReelWinPos:number[] = allWinPosMap.get(comparedReelId) || [];
            let toBeComparedModel:TheBorderReelModel = TheBorder.allReelsView.mainReelModels[comparedReelId];
            const symArr:ISymbol[] = toBeComparedModel.reelView.getSymbolsClosestToYPos(currentSym.position.y, (currentSym.sizeMultiplier * 100));
            if(symArr.length === 2) {
                if(toBeComparedReelWinPos.indexOf(symArr[0].reelPosition - 1) > -1) {
                    isTopHalf = false;
                }
                if(toBeComparedReelWinPos.indexOf(symArr[1].reelPosition - 1) > -1) {
                    isBottomHalf = false;
                }

            } else if(symArr.length) {
                if(toBeComparedReelWinPos.indexOf(symArr[0].reelPosition - 1) > -1) {
                    isTopHalf = false;
                    isBottomHalf = false;
                }
            }
        }

        const wireXPos:number = 120 * (isRightDirection ? currentReelId : currentReelId - 1) + TheBorderStatics.symbolWidth * 0.5;
        if(isTopHalf && isBottomHalf) {
            if(currentSym.sizeMultiplier === 0.5) {
                this.addHalfTape(currentSym.y - 25, wireXPos, tapeAnimations);
            } else {
                this.addFullTape(currentSym.position.y, wireXPos, tapeAnimations);
            }
        } else if(isTopHalf) {
            this.addHalfTape(currentSym.y - 25, wireXPos, tapeAnimations);
        } else if(isBottomHalf) {
            this.addHalfTape(currentSym.y + (currentSym.sizeMultiplier === 0.5 ? 25 : 50) - 25, wireXPos, tapeAnimations);
        }
    }

    /**
     * Add Tape for symbol
     * */
    private addTapeToReel(allWinPosMap:Map<number, number[]>, tapeAnimations:PIXI.Sprite[]):void {
        allWinPosMap.forEach((currentRowArr:number[], currentReelId:number) => {
            currentRowArr.forEach((currentRowId:number) => {
                const currentReelModel:TheBorderReelModel = TheBorder.allReelsView.mainReelModels[currentReelId];
                const currentSym:TheBorderSymbol = <TheBorderSymbol>currentReelModel.getSymbol(currentRowId);
                let isTop:boolean = currentRowId === 0 || currentRowArr.indexOf(currentRowId - 1) === -1; //Top condition
                let isBottom:boolean = currentRowId == TheBorder.allReelsView.getReelLength(currentReelId) || currentRowArr.indexOf(currentRowId + 1) === -1; //Bottom condition

                this.addLeftRightTape(currentSym, allWinPosMap, true, tapeAnimations); //Place Right side wire
                this.addLeftRightTape(currentSym, allWinPosMap, false, tapeAnimations); //Place Left side wire
                isTop && this.addTopTape(currentReelId, currentRowId, tapeAnimations); //Place Top side wire
                isBottom && this.addBottomTape(currentReelId, currentRowId, tapeAnimations); ////Place bottom side wire
            });
        });
    }

    /**
     * Add Left or Right wire if connected win
     * */
    private addLeftRightWire(currentSym:TheBorderSymbol, allWinPosMap:Map<number, number[]>, isRightDirection:boolean, wireAnimations:TheBorderTimelineSprite[], placedWireArr:string[]):void {
        const currentReelId:number = currentSym.reelId;
        let isTopHalf:boolean = true;
        let isBottomHalf:boolean = true;
        let comparedReelId:number;

        if(isRightDirection) {
            comparedReelId = currentReelId + 1;
        } else {
            comparedReelId = currentReelId - 1;
        }
        if(comparedReelId < 6 && comparedReelId >= 0) {
            const toBeComparedReelWinPos:number[] = allWinPosMap.get(comparedReelId) || [];
            let toBeComparedModel:TheBorderReelModel = TheBorder.allReelsView.mainReelModels[comparedReelId];
            const symArr:ISymbol[] = toBeComparedModel.reelView.getSymbolsClosestToYPos(currentSym.position.y, (currentSym.sizeMultiplier * 100));
            if(symArr.length === 2) {
                if(toBeComparedReelWinPos.indexOf(symArr[0].reelPosition - 1) > -1) {
                    isTopHalf = false;
                }
                if(toBeComparedReelWinPos.indexOf(symArr[1].reelPosition - 1) > -1) {
                    isBottomHalf = false;
                }

            } else if(symArr.length) {
                if(toBeComparedReelWinPos.indexOf(symArr[0].reelPosition - 1) > -1) {
                    isTopHalf = false;
                    isBottomHalf = false;
                }
            }
        }

        const wireXPos:number = 120 * (isRightDirection ? currentReelId : currentReelId - 1) + TheBorderStatics.symbolWidth * 0.5;
        if(isTopHalf && isBottomHalf) {
            if(currentSym.sizeMultiplier === 0.5) {
                this.addHalfWire(currentSym.y - 25, wireXPos, wireAnimations, isRightDirection, placedWireArr);
            } else {
                this.addFullWire(currentSym.position.y, wireXPos, wireAnimations, isRightDirection, placedWireArr);
            }
        } else if(isTopHalf) {
            this.addHalfWire(currentSym.y - 25, wireXPos, wireAnimations, isRightDirection, placedWireArr);
        } else if(isBottomHalf) {
            this.addHalfWire(currentSym.y + (currentSym.sizeMultiplier === 0.5 ? 25 : 50) - 25, wireXPos, wireAnimations, isRightDirection, placedWireArr);
        }
    }

    /**
     * Add Wire for symbol
     * */
    private addWireToReel(allWinPosMap:Map<number, number[]>, wireAnimations:TheBorderTimelineSprite[]):TheBorderTimelineSprite[] {
        const placedWireArr:string[] = [];
        allWinPosMap.forEach((currentRowArr:number[], currentReelId:number) => {
            currentRowArr.forEach((currentRowId:number) => {
                const currentReelModel:TheBorderReelModel = TheBorder.allReelsView.mainReelModels[currentReelId];
                const currentSym:TheBorderSymbol = <TheBorderSymbol>currentReelModel.getSymbol(currentRowId);
                let isTop:boolean = currentRowId === 0 || currentRowArr.indexOf(currentRowId - 1) === -1; //Top condition
                let isBottom:boolean = currentRowId == TheBorder.allReelsView.getReelLength(currentReelId) || currentRowArr.indexOf(currentRowId + 1) === -1; //Bottom condition

                this.addLeftRightWire(currentSym, allWinPosMap, true, wireAnimations, placedWireArr); //Place Right side wire
                this.addLeftRightWire(currentSym, allWinPosMap, false, wireAnimations, placedWireArr); //Place Left side wire
                isTop && this.addTopWire(currentReelId, currentRowId, wireAnimations, placedWireArr); //Place Top side wire
                isBottom && this.addBottomWire(currentReelId, currentRowId, wireAnimations, placedWireArr); ////Place bottom side wire
            });
        });
        return wireAnimations;
    }

    //To Add Top Wire
    private addTopWire(currentReelId:number, currentRowId:number, wireAnimations:TheBorderTimelineSprite[], placedWireArr:string[]):void {
        const searchId:string = currentReelId + "_" + currentRowId + "_" + "Top";
        if(placedWireArr.indexOf(searchId) === -1) {
            placedWireArr.push(currentReelId + "_" + currentRowId + "_" + "Top");
            let top:TheBorderTimelineSprite = this.getUnusedTopWire();
            top.position.x = TheBorderClusterWinView.WIRE_TOP_OFFSET.x + (120 * currentReelId);
            top.position.y = TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).position.y;
            top.position.y -= 50;
            wireAnimations.push(top);
        }
    }

    //To Add Bottom Wire
    private addBottomWire(currentReelId:number, currentRowId:number, wireAnimations:TheBorderTimelineSprite[], placedWireArr:string[]):void {
        const searchId:string = currentReelId + "_" + currentRowId + "_" + "Bottom";
        if(placedWireArr.indexOf(searchId) === -1) {
            placedWireArr.push(currentReelId + "_" + currentRowId + "_" + "Bottom");
            let bottom:TheBorderTimelineSprite = this.getUnusedBottomWire();
            bottom.position.x = TheBorderClusterWinView.WIRE_BOTTOM_OFFSET.x + (120 * currentReelId);
            bottom.position.y = TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).position.y;
            if(TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).sizeMultiplier == 1) {
                bottom.position.y += 50;
            }
            wireAnimations.push(bottom);
        }
    }

    //To Add full Wire
    private addFullWire(topYPos:number, topXPos:number, wireAnimations:TheBorderTimelineSprite[], isRightWire:boolean, placedWireArr:string[]):void {
        const searchId:string = topXPos + "_" + topYPos + "_" + (isRightWire ? "rightFull" : "leftFull");
        if(placedWireArr.indexOf(searchId) === -1) {
            placedWireArr.push(searchId);
            let rightSide:TheBorderTimelineSprite = isRightWire ? this.getUnusedRightWire() : this.getUnusedLeftWire();
            rightSide.position.y = topYPos;
            rightSide.position.x = topXPos;
            wireAnimations.push(rightSide)
        }
    }

    //To Add Half Wire
    private addHalfWire(topYPos:number, topXPos:number, wireAnimations:TheBorderTimelineSprite[], isRightWire:boolean, placedWireArr:string[]):void {
        const searchId:string = topXPos + "_" + topYPos + "_" + (isRightWire ? "rightHalf" : "leftHalf");
        if(placedWireArr.indexOf(searchId) === -1) {
            placedWireArr.push(searchId);
            let leftSideHalf:TheBorderTimelineSprite = isRightWire ? this.getUnusedRightHalfWire() : this.getUnusedLeftHalfWire();
            leftSideHalf.position.y = topYPos;
            leftSideHalf.position.x = topXPos;
            wireAnimations.push(leftSideHalf);
        }
    }

    private getAllTapeAnimations(xBombExplodePos:boolean[][][]):PIXI.Sprite[] {
        const nonWinExplodePosMap:Map<number, number[]> = new Map<number, number[]>();
        xBombExplodePos.forEach((reelArr:boolean[][]) => {
            reelArr.forEach((reels:boolean[], reelId:number) => {
                reels.forEach((shouldExplode:boolean, rowId:number) => {
                    if(!nonWinExplodePosMap.has(reelId)) {
                        nonWinExplodePosMap.set(reelId, []);
                    }
                    if(shouldExplode && nonWinExplodePosMap.get(reelId)?.indexOf(rowId) === -1) {
                        nonWinExplodePosMap.get(reelId)?.push(rowId);
                    }
                });
            })
        });
        const tapeAnimations:PIXI.Sprite[] = [];
        this.addTapeToReel(nonWinExplodePosMap, tapeAnimations);
        return tapeAnimations;
    }

    private createTape(isVertical:boolean = false, isHalf:boolean = false):PIXI.Sprite {
        let textureName:string = "warning_tape_full_horizontal";//"warning_tape_half_vertical";
        if(isVertical) {
            textureName = isHalf ? "warning_tape_half_vertical" : "Xbomb_warning_tape_vertical";
        }
        const tape:PIXI.Sprite = new PIXI.Sprite(TheBorder.assetLoader.getTextures(textureName)[0]);
        tape.anchor.set(0.5, 0.5);
        tape.alpha = 0;
        this._tapeContainer.addChild(tape);
        return tape;
    }

    //To Add Top Wire
    private addTopTape(currentReelId:number, currentRowId:number, tapeAnimations:PIXI.Sprite[]):void {
        const tape:PIXI.Sprite = this.createTape();
        tape.position.x = TheBorderClusterWinView.WIRE_TOP_OFFSET.x + (120 * currentReelId);
        tape.position.y = TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).position.y;
        tape.position.y -= 50;
        tapeAnimations.push(tape);
    }

    //To Add Bottom Wire
    private addBottomTape(currentReelId:number, currentRowId:number, tapeAnimations:PIXI.Sprite[]):void {
        const tape:PIXI.Sprite = this.createTape();
        tape.position.x = TheBorderClusterWinView.WIRE_BOTTOM_OFFSET.x + (120 * currentReelId);
        tape.position.y = TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).position.y;
        if(TheBorder.allReelsView.mainReelModels[currentReelId].getSymbol(currentRowId).sizeMultiplier == 1) {
            tape.position.y += 50;
        }
        tapeAnimations.push(tape);
    }

    //To Add full tape for left right
    private addFullTape(topYPos:number, topXPos:number, tapeAnimations:PIXI.Sprite[]):void {
        const tape:PIXI.Sprite = this.createTape(true);
        tape.position.y = topYPos;
        tape.position.x = topXPos;
        tapeAnimations.push(tape);
    }

    //To Add Half Wire
    private addHalfTape(topYPos:number, topXPos:number, tapeAnimations:PIXI.Sprite[]):void {
        const tape:PIXI.Sprite = this.createTape(true, true);
        tape.position.y = topYPos;
        tape.position.x = topXPos;
        tapeAnimations.push(tape);
    }
}